/**
 * TeachingCard
 * Displays a teaching ritual card matching the CoachingSessionCard style for admin
 * 640px max width, cover image with rounded corners, stats bar at bottom for owner
 */

import {
  Archive,
  ArchiveRestore,
  Bookmark,
  CircleCheck,
  Eye,
  MoreVertical,
  Pencil,
  Trash2,
  Users,
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { TagChip } from "@/components/shared/TagChip"
import { Avatar } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuthStore } from "@/features/auth/store/authStore"
import { formatRitualType } from "@/features/rituals/types"

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`
}

export interface TeachingCardProps {
  id: string
  title: string
  coverImage?: string
  type: "IN_PERSON" | "COURSE" | "SELF_GUIDED" | "SELF_GUIDED_COURSE"
  duration: number
  participationFee: "Free" | "Paid" | "Donation-based"
  price?: number | null
  description?: string
  tags?: string[]
  createdBy?: {
    id: string
    name: string | null
    username: string | null
    coverImage?: string | null
  } | null
  isBookmarked?: boolean
  onBookmarkClick?: () => void
  onEditClick?: () => void
  onDeleteClick?: () => void
  onArchiveClick?: () => void
  onUnarchiveClick?: () => void
  isArchived?: boolean
  stats?: {
    views: number
    participants: number
    completed: number
    earning: number
  } | null
}

export function TeachingCard({
  id,
  title,
  coverImage,
  type,
  duration,
  participationFee,
  price,
  description,
  tags,
  createdBy,
  isBookmarked,
  onBookmarkClick,
  onEditClick,
  onDeleteClick,
  onArchiveClick,
  onUnarchiveClick,
  isArchived,
  stats,
}: TeachingCardProps) {
  const navigate = useNavigate()
  const currentUserId = useAuthStore(state => state.user?.id)
  const isOwner = createdBy?.id === currentUserId

  const isPaid = participationFee === "Paid"
  const priceNum = price ? Number.parseFloat(String(price)) : 0
  const priceLabel =
    isPaid && priceNum > 0
      ? `$${Math.round(priceNum)}`
      : participationFee === "Donation-based"
        ? "Donation-based"
        : "Free"

  const handleCardClick = () => {
    navigate(`/rituals/${id}`)
  }

  return (
    <div
      className="relative max-w-[640px] h-full w-full overflow-hidden rounded-tl-[40px] rounded-br-[40px] border border-white/10 bg-white/5 cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Cover Image */}
      <div className="relative h-[200px] md:h-[340px] w-full overflow-hidden rounded-tl-[40px] bg-brand-slate">
        {coverImage ? (
          <img
            src={coverImage}
            alt={title}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-brand-slate" />
        )}
        <div className="absolute right-5 top-5 flex items-center gap-2">
          {/* Admin Controls */}
          {isOwner && (
            <>
              {/* Three-dot Dropdown Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    onClick={e => e.stopPropagation()}
                    className="shrink-0 cursor-pointer rounded-[10px] border border-[rgba(242,240,235,0.15)] bg-[rgba(242,240,235,0.05)] p-2.75 transition-colors hover:bg-[rgba(242,240,235,0.1)]"
                    aria-label="More options"
                  >
                    <MoreVertical className="w-[22px] h-[22px] text-primary" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="bg-white/50 backdrop-blur-sm border-white/20"
                >
                  {onEditClick && (
                    <DropdownMenuItem
                      onClick={e => {
                        e.stopPropagation()
                        onEditClick()
                      }}
                      className="text-[#000] text-[13px] hover:bg-white/10 cursor-pointer focus:bg-white/10"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                  )}
                  {onDeleteClick && (
                    <DropdownMenuItem
                      onClick={e => {
                        e.stopPropagation()
                        onDeleteClick()
                      }}
                      className="text-red-400 hover:bg-red-400/10 cursor-pointer focus:bg-red-400/10"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  )}
                  {onArchiveClick && !isArchived && (
                    <DropdownMenuItem
                      onClick={e => {
                        e.stopPropagation()
                        onArchiveClick()
                      }}
                      className="text-[#000] text-[13px] hover:bg-white/10 cursor-pointer focus:bg-white/10"
                    >
                      <Archive className="w-4 h-4 mr-2" />
                      Archive
                    </DropdownMenuItem>
                  )}
                  {onUnarchiveClick && isArchived && (
                    <DropdownMenuItem
                      onClick={e => {
                        e.stopPropagation()
                        onUnarchiveClick()
                      }}
                      className="text-[#000] text-[13px] hover:bg-white/10 cursor-pointer focus:bg-white/10"
                    >
                      <ArchiveRestore className="w-4 h-4 mr-2" />
                      Unarchive
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}

          {/* Bookmark Button */}
          {onBookmarkClick && !isOwner && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                onBookmarkClick()
              }}
              className="shrink-0 cursor-pointer rounded-[10px] border border-[rgba(242,240,235,0.15)] bg-[rgba(242,240,235,0.05)] p-2.75 transition-colors hover:bg-[rgba(242,240,235,0.1)]"
              aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
            >
              <Bookmark
                className={`w-[22px] h-[22px] ${isBookmarked ? "fill-white text-white" : "text-primary"}`}
              />
            </button>
          )}
        </div>
      </div>

      <div className="p-5">
        {/* Title */}
        <h3 className="text-sm md:text-[18px] font-semibold leading-5.5 text-[#F2F0EB] line-clamp-1">
          {title}
        </h3>
        <div className="flex items-center gap-2.5 mt-2.5">
          <span
            className={`rounded-[5px] px-1.5 py-0.5 text-[13px] leading-5 text-connect-cream opacity-90 ${
              isPaid
                ? "bg-brand-terracotta"
                : participationFee === "Donation-based"
                  ? "bg-brand-terracotta"
                  : "bg-brand-olive"
            }`}
          >
            {priceLabel}
          </span>
          <span className="text-[13px] leading-5 text-[#F2F0EB]">{formatRitualType(type)}</span>
          <span className="text-[13px] leading-normal text-[#F2F0EB]">
            {formatDuration(duration)}
          </span>
        </div>

        {/* Creator Profile */}
        {createdBy && !isOwner && (
          <div className="flex items-center gap-2.5 my-5">
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                navigate(`/profile/${createdBy.id}`)
              }}
              className="cursor-pointer rounded-full border border-white/20"
              aria-label={`View ${createdBy.name || createdBy.username}'s profile`}
            >
              <Avatar
                src={createdBy.coverImage ?? undefined}
                fallback={createdBy.name || createdBy.username || "?"}
                size="sm"
                className="w-7.5 h-7.5"
              />
            </button>
            <span className="text-sm font-semibold text-[#F2F0EB] leading-5.5">
              {createdBy.name || createdBy.username}
            </span>
          </div>
        )}

        {/* Description */}
        {description && !isOwner && (
          <p className="text-[13px] leading-5 text-[#F2F0EB] line-clamp-2">{description}</p>
        )}

        {/* Tags Row */}
        {!isOwner && (
          <div className="flex flex-wrap items-center gap-2.5 pt-5">
            {tags && tags.length > 0 && (
              <>
                {tags.slice(0, 3).map(tag => (
                  <TagChip
                    key={tag}
                    tag={tag}
                    tab="sessions"
                    className="border border-[#F2F0EB] rounded-lg p-2.5 text-[13px] leading-5 text-[#F2F0EB]"
                  />
                ))}
                {tags?.length > 3 && (
                  <span className="text-sm text-[#F2F0EB]">+{tags?.length - 3}</span>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Owner stats - only show when real stats are available via props */}
      {isOwner && (
        <div className="p-5 bg-[#F2F0EB1A] md:block hidden">
          <div className="text-[16px] leading-normal text-[#F2F0EB99] flex flex-wrap gap-x-5 gap-y-1">
            <span>
              Views · <span className="font-semibold">{stats?.views ?? 0}</span>
            </span>
            <span>
              Participants · <span className="font-semibold">{stats?.participants ?? 0}</span>
            </span>
            <span>
              Completed · <span className="font-semibold">{stats?.completed ?? 0}</span>
            </span>
            <span>
              Earnings · <span className="font-semibold">${stats?.earning ?? 0}</span>
            </span>
          </div>
        </div>
      )}
      {isOwner && (
        <div className="p-5 bg-[#F2F0EB1A] md:hidden">
          <div className="text-[16px] leading-normal text-[#F2F0EB99] flex flex-wrap gap-x-5 gap-y-1">
            <span className="flex items-center gap-2.5">
              <Eye className="w-5 h-5" />{" "}
              <span className="font-semibold text-primary">{stats?.views ?? 0}</span>
            </span>
            <span className="flex items-center gap-2.5">
              <Users className="w-5 h-5" />{" "}
              <span className="font-semibold text-primary">{stats?.participants ?? 0}</span>
            </span>
            <span className="flex items-center gap-2.5">
              <Bookmark className="w-5 h-5" />{" "}
              <span className="font-semibold text-primary">{stats?.completed ?? 0}</span>
            </span>
            <span className="flex items-center gap-2.5">
              <CircleCheck className="w-5 h-5" />{" "}
              <span className="font-semibold text-primary">${stats?.earning ?? 0}</span>
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
